import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/axios";
import type { BusinessEventDto, BusinessEventFilters, BusinessEventStatsDto } from "./businessEventTypes";

type EventListResponse = {
  data?: BusinessEventDto[];
  meta?: { nextCursor: number | null; hasMore: boolean };
};

type StatsResponse = {
  data?: BusinessEventStatsDto;
};

const DEFAULT_STATS: BusinessEventStatsDto = {
  today: 0,
  critical: 0,
  blocked: 0,
  reassignments: 0,
  validations: 0,
  gpsSuspects: 0,
};

export function useBusinessEventsLiveFeed(filters: BusinessEventFilters) {
  const [items, setItems] = useState<BusinessEventDto[]>([]);
  const [stats, setStats] = useState<BusinessEventStatsDto>(DEFAULT_STATS);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim()) params.set(key, value.trim());
    });
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsResponse, statsResponse] = await Promise.all([
        api.get<EventListResponse>(`/admin/business-events?${queryParams.toString()}`),
        api.get<StatsResponse>("/admin/business-events/stats"),
      ]);
      setItems(Array.isArray(eventsResponse.data.data) ? eventsResponse.data.data : []);
      setNextCursor(eventsResponse.data.meta?.nextCursor ?? null);
      setHasMore(Boolean(eventsResponse.data.meta?.hasMore));
      setStats(statsResponse.data.data ?? DEFAULT_STATS);
    } catch (e) {
      console.error(e);
      setItems([]);
      setError("Impossible de charger le journal opérationnel.");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams(queryParams);
      params.set("cursorId", String(nextCursor));
      const { data } = await api.get<EventListResponse>(`/admin/business-events?${params.toString()}`);
      const next = Array.isArray(data.data) ? data.data : [];
      setItems((current) => [...current, ...next]);
      setNextCursor(data.meta?.nextCursor ?? null);
      setHasMore(Boolean(data.meta?.hasMore));
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, nextCursor, queryParams]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(id);
  }, [load]);

  return { items, stats, loading, loadingMore, error, hasMore, load, loadMore };
}
